import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Divider,
  Paper
} from '@mui/material';
import {
  Support as SupportIcon,
  Add as AddIcon,
  Message as MessageIcon,
  BugReport as BugIcon,
  Help as HelpIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  doc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import dayjs from 'dayjs';

const ticketTypes = [
  { value: 'bug', label: 'Signaler un bug', icon: <BugIcon /> },
  { value: 'feature', label: 'Demande de fonctionnalité', icon: <SettingsIcon /> },
  { value: 'help', label: 'Aide / Question', icon: <HelpIcon /> },
  { value: 'billing', label: 'Facturation', icon: <MessageIcon /> },
  { value: 'other', label: 'Autre', icon: <MessageIcon /> }
];

const SupportTab = ({ userProfile, setSuccess, setError }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketForm, setTicketForm] = useState({
    type: '',
    subject: '',
    description: '',
    priority: 'medium'
  });

  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser) return;

    // Subscribe to user's tickets
    const q = query(
      collection(db, 'support_tickets'),
      where('userId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const ticketsData = [];
      querySnapshot.forEach((doc) => {
        ticketsData.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt),
          updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : new Date(doc.data().updatedAt)
        });
      });
      setTickets(ticketsData);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const openTicketDialog = () => {
    setTicketForm({
      type: '',
      subject: '',
      description: '',
      priority: 'medium'
    });
    setTicketDialogOpen(true);
  };

  const createTicket = async () => {
    if (!ticketForm.type || !ticketForm.subject || !ticketForm.description) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      const ticketData = {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        salonName: userProfile.salonName,
        type: ticketForm.type,
        subject: ticketForm.subject,
        description: ticketForm.description,
        priority: ticketForm.priority,
        status: 'open',
        createdAt: new Date(),
        updatedAt: new Date(),
        messages: [
          {
            id: Date.now().toString(),
            sender: 'user',
            senderName: userProfile.ownerName || userProfile.salonName,
            message: ticketForm.description,
            timestamp: new Date()
          }
        ]
      };

      await addDoc(collection(db, 'support_tickets'), ticketData);
      
      setSuccess('Ticket de support créé avec succès!');
      setTicketDialogOpen(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error creating ticket:', error);
      setError('Erreur lors de la création du ticket');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'warning';
      case 'in_progress': return 'info';
      case 'resolved': return 'success';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'open': return 'Ouvert';
      case 'in_progress': return 'En cours';
      case 'resolved': return 'Résolu';
      case 'closed': return 'Fermé';
      default: return status;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      default: return 'default';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'low': return 'Faible';
      case 'medium': return 'Moyenne';
      case 'high': return 'Élevée';
      default: return priority;
    }
  };

  const getTypeIcon = (type) => {
    const ticketType = ticketTypes.find(t => t.value === type);
    return ticketType ? ticketType.icon : <MessageIcon />;
  };

  const getTypeLabel = (type) => {
    const ticketType = ticketTypes.find(t => t.value === type);
    return ticketType ? ticketType.label : type;
  };

  const viewTicketDetails = (ticket) => {
    setSelectedTicket(ticket);
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5" display="flex" alignItems="center" gap={1}>
          <SupportIcon />
          Support
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openTicketDialog}
          sx={{ 
            backgroundColor: '#f5f5dc',
            color: 'black',
            '&:hover': {
              backgroundColor: '#e8e8d0'
            }
          }}
        >
          Nouveau ticket
        </Button>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Besoin d'aide ? Créez un ticket de support et notre équipe vous répondra rapidement.
      </Typography>

      {/* Quick Help */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Aide rapide
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Questions fréquentes :
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Comment modifier mes horaires d'ouverture ?<br/>
                • Comment ajouter des photos à mon salon ?<br/>
                • Comment gérer mes réservations ?<br/>
                • Comment partager ma fiche salon ?
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Contact direct :
              </Typography>
              <Typography variant="body2" color="text.secondary">
                📧 support@omnivers.fr<br/>
                📞 01 23 45 67 89<br/>
                🕒 Lun-Ven 9h-18h
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tickets List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Mes tickets de support ({tickets.length})
          </Typography>
          
          {tickets.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="text.secondary">
                Aucun ticket de support pour le moment
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={openTicketDialog}
                sx={{ mt: 2 }}
              >
                Créer mon premier ticket
              </Button>
            </Box>
          ) : (
            <List>
              {tickets.map((ticket, index) => (
                <React.Fragment key={ticket.id}>
                  <ListItem 
                    button 
                    onClick={() => viewTicketDetails(ticket)}
                    sx={{ 
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        backgroundColor: '#f5f5f5'
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" mr={2}>
                      {getTypeIcon(ticket.type)}
                    </Box>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1">
                            {ticket.subject}
                          </Typography>
                          <Chip 
                            label={getStatusLabel(ticket.status)}
                            color={getStatusColor(ticket.status)}
                            onClick={() => {}}
                            size="small"
                          />
                          <Chip 
                            label={getPriorityLabel(ticket.priority)}
                            color={getPriorityColor(ticket.priority)}
                            onClick={() => {}}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {getTypeLabel(ticket.type)} • {dayjs(ticket.createdAt).format('DD/MM/YYYY à HH:mm')}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {ticket.description.substring(0, 100)}
                            {ticket.description.length > 100 && '...'}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < tickets.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Create Ticket Dialog */}
      <Dialog open={ticketDialogOpen} onClose={() => setTicketDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Créer un ticket de support
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Type de demande</InputLabel>
                <Select
                  value={ticketForm.type}
                  label="Type de demande"
                  onChange={(e) => setTicketForm(prev => ({ ...prev, type: e.target.value }))}
                >
                  {ticketTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {type.icon}
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priorité</InputLabel>
                <Select
                  value={ticketForm.priority}
                  label="Priorité"
                  onChange={(e) => setTicketForm(prev => ({ ...prev, priority: e.target.value }))}
                >
                  <MenuItem value="low">Faible</MenuItem>
                  <MenuItem value="medium">Moyenne</MenuItem>
                  <MenuItem value="high">Élevée</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Sujet"
                value={ticketForm.subject}
                onChange={(e) => setTicketForm(prev => ({ ...prev, subject: e.target.value }))}
                required
                placeholder="Résumez votre demande en quelques mots"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description détaillée"
                multiline
                rows={6}
                value={ticketForm.description}
                onChange={(e) => setTicketForm(prev => ({ ...prev, description: e.target.value }))}
                required
                placeholder="Décrivez votre problème ou votre demande en détail. Plus vous donnez d'informations, plus nous pourrons vous aider efficacement."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTicketDialogOpen(false)}>
            Annuler
          </Button>
          <Button onClick={createTicket} variant="contained" disabled={loading}>
            {loading ? 'Création...' : 'Créer le ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ticket Details Dialog */}
      <Dialog 
        open={!!selectedTicket} 
        onClose={() => setSelectedTicket(null)} 
        maxWidth="md" 
        fullWidth
      >
        {selectedTicket && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={1}>
                {getTypeIcon(selectedTicket.type)}
                {selectedTicket.subject}
                <Chip 
                  label={getStatusLabel(selectedTicket.status)}
                  color={getStatusColor(selectedTicket.status)}
                  onClick={() => {}}
                  size="small"
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Créé le {dayjs(selectedTicket.createdAt).format('DD/MM/YYYY à HH:mm')} • 
                  Priorité: {getPriorityLabel(selectedTicket.priority)} • 
                  Type: {getTypeLabel(selectedTicket.type)}
                </Typography>
              </Box>
              
              <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                <Typography variant="body1">
                  {selectedTicket.description}
                </Typography>
              </Paper>

              {selectedTicket.messages && selectedTicket.messages.length > 1 && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>
                    Conversation
                  </Typography>
                  {selectedTicket.messages.slice(1).map((message, index) => (
                    <Paper key={index} elevation={1} sx={{ p: 2, mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {message.senderName} • {dayjs(message.timestamp.toDate()).format('DD/MM/YYYY à HH:mm')}
                      </Typography>
                      <Typography variant="body1">
                        {message.message}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedTicket(null)}>
                Fermer
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default SupportTab;